import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { Capacitor } from '@capacitor/core';
import { checkForOtaUpdate } from './services/otaUpdateService';
import { SafeArea, SystemBarsStyle, SystemBarsType } from '@capacitor-community/safe-area';
import { initializeNotifications, rescheduleAllReminders, registerFCMToken, scheduleReminderNotification } from './services/notificationService';
import { getAllTasks, updateTask, getTask} from './services/taskService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './lib/firebase';
import { migrateQuestionnaireTemplates } from './utils/migrateQuestionnaireTemplates';
import { migrateChecklistTemplates } from './utils/migrateChecklistTemplates';


// Expose migration functions for browser console (development only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).migrateQuestionnaireTemplates = migrateQuestionnaireTemplates;
  (window as unknown as Record<string, unknown>).migrateChecklistTemplates = migrateChecklistTemplates;
}
// Configure System Bars via @capacitor-community/safe-area
// This replaces @capacitor/status-bar and handles all Android screen modes:
// normal, edge-to-edge, fullscreen, immersive, display cutouts, keyboard changes
if (Capacitor.isNativePlatform()) {
  // Set status bar style: Dark = light/white icons (for our dark #2d5a6b theme color)
  SafeArea.setSystemBarsStyle({
    style: SystemBarsStyle.Dark,
    type: SystemBarsType.StatusBar
  }).catch(err => console.warn('Failed to set status bar style:', err));

  // Set navigation bar style: Default = auto-adapts to device theme
  SafeArea.setSystemBarsStyle({
    style: SystemBarsStyle.Default,
    type: SystemBarsType.NavigationBar
  }).catch(err => console.warn('Failed to set nav bar style:', err));
  
  // Initialize unified notification service (requests permissions, creates channels, sets up listeners)
  initializeNotifications(
    // Notification tap handler (when tapping notification body)
    (notification) => {
      console.log('Notification tapped:', notification);
      
      // Handle notification tap navigation
      if (notification.type === 'local' && notification.data?.taskId) {
        // Navigate to task detail or dashboard
        // This will be handled by storing the taskId and routing in App.tsx
        const event = new CustomEvent('notificationTap', { 
          detail: { taskId: notification.data.taskId } 
        });
        window.dispatchEvent(event);
      } else if (notification.type === 'fcm' && notification.data) {
        // Handle FCM notification tap based on data
        const event = new CustomEvent('notificationTap', { 
          detail: notification.data 
        });
        window.dispatchEvent(event);
      }
    },
    // Task completion handler (for "Mark Done" button)
    async (taskId: string) => {
      console.log('🔔 Task completion handler called for taskId:', taskId);
      try {
        console.log('Updating task with completed: true');
        await updateTask(taskId, { 
          completed: true
        });
        console.log('✅ Task marked as complete successfully');
      } catch (error) {
        console.error('❌ Failed to mark task as complete:', error);
        throw error;
      }
    },
    // Remind later handler (for "Remind Later" button - postpone by 10 minutes)
    async (taskId: string) => {
      console.log('⏰ Remind later handler called for taskId:', taskId);
      try {
        // Get the current task
        const task = await getTask(taskId);
        if (!task) {
          console.error('Task not found:', taskId);
          return;
        }

        // Default snooze duration: 10 minutes
        const snoozeDuration = 10 * 60 * 1000;
        const newDueDate = new Date(Date.now() + snoozeDuration);
        console.log('Updating task due date to:', newDueDate);
        
        // Track snooze history
        const snoozeCount = (task.snoozeCount || 0) + 1;
        const originalDueDate = task.originalDueDate || task.dueDate;
        
        // Update task with new due date and snooze tracking
        await updateTask(taskId, { 
          dueDate: newDueDate,
          originalDueDate: originalDueDate,
          snoozeCount: snoozeCount,
          lastSnoozedAt: new Date(),
        });
        
        // Reschedule the notification
        await scheduleReminderNotification({ 
          ...task, 
          dueDate: newDueDate,
          originalDueDate: originalDueDate,
          snoozeCount: snoozeCount,
          lastSnoozedAt: new Date(),
        });
        
        console.log(`✅ Task reminder snoozed (${snoozeCount}x) to ${newDueDate.toLocaleTimeString()}`);
      } catch (error) {
        console.error('❌ Failed to postpone reminder:', error);
        throw error;
      }
    }
  ).catch(err => {
    console.error('Failed to initialize notifications:', err);
  });
  
  // Reschedule pending reminders only once, after the user is authenticated.
  // Using a one-time listener so we don't reschedule again on subsequent auth changes.
  const unsubscribeReschedule = onAuthStateChanged(auth, (firebaseUser) => {
    if (!firebaseUser) return; // Not logged in – skip, no notifications needed
    unsubscribeReschedule(); // Run once only
    getAllTasks().then(tasks => {
      if (tasks && tasks.length > 0) {
        rescheduleAllReminders(tasks).catch(err => {
          console.error('Failed to reschedule reminders on startup:', err);
        });
      }
    }).catch(err => {
      console.error('Failed to fetch tasks on startup:', err);
    });
  });
}

// Rendering waits for checkForOtaUpdate() to finish. Its internal 5s timeout
// only applies to the update-metadata fetch; if a new bundle is available,
// downloading it may block startup longer before LiveUpdate.reload() restarts
// the webview, and createRoot().render() is never reached for this session.
(async () => {
  await checkForOtaUpdate();
  createRoot(document.getElementById("root")!).render(<App />);
})();
